/**
 * The console's themes (spec docs/superpowers/specs/2026-09-27-admin-console-themes-design.md).
 *
 * The console — and only the console — has its own themes, switched on by `data-admin-theme` on
 * <html> while a console page is open: five ready-made ones, and Custom, which the admin builds.
 * The play area, the learner portal and the customer app keep their own look and never read this
 * file.
 *
 * This file is data only; parsing and writing the saved choice live beside it (admin-ui.ts). It
 * imports nothing and re-exports nothing (review A3): every other file here imports it, so it must
 * never import them back — under ES modules a cycle here throws a TDZ ReferenceError whenever this
 * file is loaded first. __tests__/admin-theme-imports.guard.spec.ts holds it. The API keeps its own
 * copy of the keys and modes for validation, and a parity spec compares the two.
 */

export const ADMIN_THEMES = ['classic', 'ledger', 'sentinel', 'harbor', 'clarity'] as const
export type AdminThemeKey = (typeof ADMIN_THEMES)[number]

export const ADMIN_MODES = ['light', 'dark', 'system'] as const
export type AdminMode = (typeof ADMIN_MODES)[number]

// ── Custom: the admin's own theme, built in the picker ────────────────────────────────────────────
//
// Owner, 2026-09-27: beside the ready-made themes the console keeps the theme builder the customer
// app has ("Theme Settings", components/theme-switcher.tsx) — a colour, a style and a sidebar, on
// Classic's tokens. The choices and their values are the customer app's (lib/constants/themes.ts,
// app/globals.css); the console keeps its own copy, saved with the admin, and
// __tests__/admin-custom-theme.spec.ts holds the two equal.

/** The admin's own theme: Classic, with the builder's colour, style and sidebar. */
export const ADMIN_CUSTOM_THEME = 'custom' as const

/** Every value a saved `theme` may take: a ready-made theme, or the admin's own. */
export const ADMIN_THEME_CHOICES = [...ADMIN_THEMES, ADMIN_CUSTOM_THEME] as const
export type AdminThemeChoice = (typeof ADMIN_THEME_CHOICES)[number]

/** The builder's colour grid — the customer app's THEME_COLORS, by name and in its order. */
export const ADMIN_CUSTOM_COLORS = ['default', 'blue', 'green', 'red', 'rose', 'violet', 'orange', 'yellow', 'teal'] as const
export type AdminCustomColor = (typeof ADMIN_CUSTOM_COLORS)[number]

/** Each grid colour as its swatch paints it (THEME_COLORS' values). */
export const ADMIN_CUSTOM_COLOR_VALUES: Record<AdminCustomColor, string> = {
  default: 'oklch(0.205 0 0)',
  blue: 'oklch(0.623 0.214 259.815)',
  green: 'oklch(0.723 0.219 149.579)',
  red: 'oklch(0.637 0.237 25.331)',
  rose: 'oklch(0.645 0.246 16.439)',
  violet: 'oklch(0.606 0.25 292.717)',
  orange: 'oklch(0.705 0.213 47.604)',
  yellow: 'oklch(0.795 0.184 86.047)',
  teal: 'oklch(0.60 0.118 184.704)',
}

/** `color` is a grid colour, or this: the admin's own colour, `hex`. */
export const ADMIN_CUSTOM_OWN_COLOR = 'custom' as const

/** Corners and scale — the customer app's THEME_TYPES. */
export const ADMIN_CUSTOM_STYLES = ['default', 'scaled', 'mono'] as const
export type AdminCustomStyle = (typeof ADMIN_CUSTOM_STYLES)[number]

/** How the sidebar sits — the customer app's SIDEBAR_VARIANTS. `default` is docked. */
export const ADMIN_CUSTOM_SIDEBARS = ['default', 'floating', 'inset'] as const
export type AdminCustomSidebar = (typeof ADMIN_CUSTOM_SIDEBARS)[number]

/** How many colours the builder remembers under "Recent". */
export const ADMIN_CUSTOM_RECENT_MAX = 8

/** The builder's settings. */
export interface AdminCustomTheme {
  /** A grid colour, or `custom` for the admin's own colour in `hex`. */
  color: AdminCustomColor | typeof ADMIN_CUSTOM_OWN_COLOR
  /** The admin's own colour: six lowercase hex digits, or null. Kept while a grid colour is chosen, as the customer app keeps it. */
  hex: string | null
  style: AdminCustomStyle
  sidebar: AdminCustomSidebar
  /** Colours the admin made, most recent first: six lowercase hex digits each, at most ADMIN_CUSTOM_RECENT_MAX. */
  recent: string[]
}

/** What the builder starts from: Classic's colour, the default style, the sidebar docked, nothing recent. */
export const DEFAULT_ADMIN_CUSTOM: Readonly<AdminCustomTheme> = Object.freeze({
  color: 'default',
  hex: null,
  style: 'default',
  sidebar: 'default',
  recent: [] as string[],
})

/** The Custom option in the picker. */
export const ADMIN_CUSTOM_META = {
  key: ADMIN_CUSTOM_THEME,
  label: 'Custom',
  description: 'Build your own on Classic: a colour, a corner style and how the sidebar sits.',
} as const

export interface AdminUi {
  theme: AdminThemeChoice
  mode: AdminMode
  /**
   * The builder's settings — kept while a ready-made theme is chosen, so Custom comes back as the
   * admin left it. Absent: the builder's defaults (DEFAULT_ADMIN_CUSTOM).
   */
  custom?: AdminCustomTheme
}

export const DEFAULT_ADMIN_UI: AdminUi = { theme: 'classic', mode: 'system' }

/**
 * What a save may name (review A2, 2026-09-27): PUT admin/me/ui is sent only what changed — any of
 * these, and within `custom` any of the builder's fields — and the API merges it into what it
 * stores. The API's request DTOs declare exactly these; parity specs on both sides compare them.
 */
export const ADMIN_UI_FIELDS = ['theme', 'mode', 'custom'] as const
export const ADMIN_CUSTOM_FIELDS = ['color', 'hex', 'style', 'sidebar', 'recent'] as const

/** Mirrors the admin's saved choice for the first paint. Path=/admin, so it never leaves the console. */
export const ADMIN_UI_COOKIE = 'hf-admin-ui'

/** Dispatched on window when the admin picks another theme, mode or builder setting. */
export const ADMIN_UI_CHANGE_EVENT = 'hf-admin-ui-change'

/** The CSS variable next/font sets for Inter (the Ledger font), on <html> while the console is open. */
export const ADMIN_INTER_FONT_VARIABLE = '--font-admin-inter'

export interface AdminThemeMeta {
  key: AdminThemeKey
  label: string
  /** One line, plain words: what the theme is for. */
  description: string
  /** Shown as a hint only; never changes the admin's own light/dark choice. */
  bestIn?: 'light' | 'dark'
  /** Canvas, accent, second chart colour — for the picker's cards. */
  swatches: [string, string, string]
  font: 'geist' | 'inter'
  sidebar: 'sidebar' | 'floating'
}

export const ADMIN_THEME_META: Record<AdminThemeKey, AdminThemeMeta> = {
  classic: {
    key: 'classic',
    label: 'Classic',
    description: 'The console as it looks today: soft corners and white panels.',
    swatches: ['oklch(1 0 0)', 'oklch(0.205 0 0)', 'oklch(0.65 0.25 250)'],
    font: 'geist',
    sidebar: 'sidebar',
  },
  ledger: {
    key: 'ledger',
    label: 'Ledger',
    description: 'Boxy and high-contrast: a grey canvas, white panels, crisp borders and square corners.',
    swatches: ['oklch(0.967 0 0)', 'oklch(0.47 0.2 262)', 'oklch(0.6 0.12 190)'],
    font: 'inter',
    sidebar: 'sidebar',
  },
  sentinel: {
    key: 'sentinel',
    label: 'Sentinel',
    description: 'An operations console: deep navy, a cyan signal colour and figures in a monospaced face.',
    bestIn: 'dark',
    swatches: ['oklch(0.15 0.02 255)', 'oklch(0.8 0.13 190)', 'oklch(0.8 0.14 75)'],
    font: 'geist',
    sidebar: 'sidebar',
  },
  harbor: {
    key: 'harbor',
    label: 'Harbor',
    description: 'Calm and roomy: round corners, generous spacing and a sea-green accent.',
    swatches: ['oklch(0.975 0.008 170)', 'oklch(0.53 0.1 172)', 'oklch(0.7 0.14 35)'],
    font: 'geist',
    sidebar: 'floating',
  },
  clarity: {
    key: 'clarity',
    label: 'Clarity',
    description: 'Accessibility first: the strongest contrast, a thick focus ring and slightly larger text.',
    swatches: ['oklch(1 0 0)', 'oklch(0.42 0.19 264)', '#E69F00'],
    font: 'geist',
    sidebar: 'sidebar',
  },
}
